import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, eur, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, RefreshCw, Trash2, CalendarPlus, Receipt, FileDown, Mail } from "lucide-react";

export default function Contratti() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const customerFilter = params.get("customer");
  const plantFilter = params.get("plant");
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () =>
    api.get("/contracts").then(({ data }) =>
      setRows(customerFilter ? data.filter((c) => c.customer_id === customerFilter) : data));
  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/plants").then(({ data }) => setPlants(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerFilter]);

  const fields = [
    { name: "code", label: "Codice contratto (vuoto = automatico)" },
    {
      name: "customer_id", label: "Cliente", type: "select", required: true,
      options: customers.map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: "plant_id", label: "Impianto (opzionale)", type: "select", full: true,
      options: plants.map((p) => ({ value: p.id, label: `${p.name} — ${p.customer_name}` })),
    },
    { name: "start_date", label: "Inizio", type: "date", required: true },
    { name: "end_date", label: "Scadenza", type: "date", required: true },
    { name: "amount", label: "Importo annuo €", type: "number" },
    { name: "frequency_months", label: "Frequenza manutenzione (mesi)", type: "number" },
    { name: "included_visits", label: "Visite incluse", type: "number" },
    {
      name: "status", label: "Stato", type: "select",
      options: ["attivo", "scaduto", "rinnovato", "disdetto"].map((s) => ({ value: s, label: s })),
    },
    { name: "auto_renew", label: "Rinnovo automatico", type: "switch", hint: "Rinnova alla scadenza" },
    { name: "notes", label: "Note", type: "textarea", full: true },
  ];

  const save = async (values) => {
    try {
      if (editing) await api.put(`/contracts/${editing.id}`, values);
      else await api.post("/contracts", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const renew = async (c) => {
    await api.post(`/contracts/${c.id}/renew`, { months: 12 });
    setMsg(`Contratto ${c.code} rinnovato per 12 mesi.`);
    load();
  };

  const generate = async (c) => {
    const { data } = await api.post("/maintenance/generate", { contract_id: c.id });
    setMsg(`Generate ${data.created} scadenze di manutenzione da ${c.code}.`);
  };

  const doInvoice = async (c) => {
    try {
      await api.post(`/contracts/${c.id}/invoice`);
      setMsg(`Contratto ${c.code} fatturato.`);
      await load();
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };

  const downloadInvoicePdf = async (c) => {
    try {
      const { data } = await api.get(`/contracts/${c.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.invoice_code || "proforma"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg("Download della proforma non riuscito.");
    }
  };

  const sendInvoice = async (c) => {
    try {
      const { data } = await api.post(`/contracts/${c.id}/send-invoice`);
      setMsg(`Proforma di ${c.code} inviata a ${data.sent_to}.`);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };


  return (
    <div data-testid="contratti-page">
      <PageHeader title="Contratti di manutenzione" subtitle={`${rows.length} contratti`}>
        <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-contract-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuovo contratto
        </Button>
      </PageHeader>

      {msg && (
        <p className="mb-4 text-sm font-medium text-primary" data-testid="contract-message">{msg}</p>
      )}

      <ListCard testid="contratti-list">
        {rows.map((c) => (
          <div key={c.id} className="px-4 sm:px-6 py-4 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-muted-foreground">{c.code}</span>
                <p className="font-semibold">{c.customer_name}</p>
                <Badge className="rounded-sm capitalize" variant={c.status === "attivo" ? "default" : "secondary"}>
                  {c.status}
                </Badge>
                {c.expiring && (
                  <Badge className="rounded-sm bg-destructive text-destructive-foreground">
                    scade in {c.days_to_expiry} gg
                  </Badge>
                )}
                {c.invoice_status && (
                  <Badge variant={c.invoice_status === "incassato" ? "secondary" : "outline"}
                         className="rounded-sm capitalize">
                    {c.invoice_status.replace("_", " ")}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {itDate(c.start_date)} → {itDate(c.end_date)} · {eur(c.amount)}/anno · ogni {c.frequency_months} mesi ·
                {c.auto_renew ? " rinnovo automatico" : " rinnovo manuale"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => generate(c)}
                      data-testid={`generate-maintenance-${c.id}`}>
                <CalendarPlus className="h-3.5 w-3.5 mr-2" /> Genera scadenze
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => renew(c)}
                      data-testid={`renew-contract-${c.id}`}>
                <RefreshCw className="h-3.5 w-3.5 mr-2" /> Rinnova
              </Button>
              {user?.role === "azienda" && c.invoice_status === "da_fatturare" && (
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => doInvoice(c)}
                        data-testid={`invoice-contract-${c.id}`}>
                  <Receipt className="h-3.5 w-3.5 mr-2" /> Fattura
                </Button>
              )}
              {user?.role === "azienda" && c.invoice_status && c.invoice_status !== "da_fatturare" && (
                <>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => downloadInvoicePdf(c)}
                          data-testid={`invoice-pdf-contract-${c.id}`}>
                    <FileDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => sendInvoice(c)}
                          data-testid={`send-invoice-contract-${c.id}`}>
                    <Mail className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" className="rounded-sm"
                      onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-contract-${c.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                      onClick={async () => { await api.delete(`/contracts/${c.id}`); load(); }}
                      data-testid={`delete-contract-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessun contratto." />}
      </ListCard>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica contratto" : "Nuovo contratto"}
                  fields={fields}
                  initial={editing || {
                    status: "attivo", frequency_months: 6, auto_renew: true,
                    ...(customerFilter ? { customer_id: customerFilter } : {}),
                    ...(plantFilter ? { plant_id: plantFilter } : {}),
                  }}
                  onSubmit={save} testid="contract-dialog" />
    </div>
  );
}
